import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Close as CloseIcon } from "@mui/icons-material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";

import { useGetVersionStatusQuery } from "fond/api";
import { canUpdateDesign, getCurrentProject } from "fond/project/redux";
import { useProjectQuota } from "fond/project/solve/hooks/useProjectQuota";
import { run } from "fond/project/solve/redux";
import { Store } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { getDefaultWorkflowQuality } from "fond/utils/project";

const DesignNeedsUpdating: React.FC = () => {
  const dispatch = useAppDispatch();
  const [snackOpen, setSnackOpen] = useState(true);
  const versionId = useSelector((state: Store): string => state.project.versionId);
  const { data: versionStatus } = useGetVersionStatusQuery(versionId, { skip: !versionId });
  const canUpdate = useSelector((state: Store) => canUpdateDesign(state.project, versionStatus, null));
  const project = useSelector((state: Store) => getCurrentProject(state.project));
  const { quota: premQuota } = useProjectQuota(project ?? null);
  const hasQuota = premQuota && premQuota.AvailableQuota > 0;

  if (!snackOpen) {
    return null;
  }

  if (canUpdate && hasQuota) {
    return (
      <div data-testid="design-needs-updating">
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          message="You have made changes that may affect your design. You can apply them when you're ready."
          action={[
            <Button
              key="update"
              color="primary"
              size="small"
              className="apply-changes"
              onClick={() => {
                setSnackOpen(false);
                dispatch(run(versionId, versionStatus?.WorkflowQuality || getDefaultWorkflowQuality(project)));
              }}
            >
              Update design
            </Button>,
            <IconButton
              key="close"
              color="inherit"
              onClick={() => {
                setSnackOpen(false);
              }}
              size="large"
            >
              <CloseIcon />
            </IconButton>,
          ]}
          open
          onClose={(event, reason) => {
            if (reason !== "clickaway") {
              setSnackOpen(false);
            }
          }}
        />
      </div>
    );
  } else {
    return null;
  }
};

export default DesignNeedsUpdating;
